import { useMemo } from 'react'

import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'

import { Filter } from '../menu/filterSection.interface'

/**
 * Calculate total selected filters including URL-based selections
 * Uses a consistent calculation method between server and client to prevent hydration mismatches
 */
export function useEnhancedFilterCount(
  selectedFiltersState: Record<string, string[]>,
  showGroupedPage: boolean,
  routerAsPath: string,
  serverFilters: Filter[],
) {
  // Calculate total count in a single useMemo to ensure consistency
  return useMemo(() => {
    // Calculate base count from selectedFiltersState
    const baseCount = Object.values(selectedFiltersState).reduce((total: number, filterValues) => {
      if (!filterValues || !Array.isArray(filterValues)) return total
      return total + filterValues.length
    }, 0)

    // For non-grouped pages or missing data, return only base count
    if (!showGroupedPage || !routerAsPath || !serverFilters?.length) {
      return baseCount
    }

    // Calculate URL-based count consistently
    const formattedPathname = routerAsPath.startsWith('/') ? routerAsPath.slice(1) : routerAsPath
    const pathSegments = formattedPathname.split('/')
    const lastSegment = pathSegments[pathSegments.length - 1]

    if (!lastSegment) {
      return baseCount
    }

    // Normalize the last segment once
    const normalizedLastSegment = lastSegment
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '')

    // Check if the last segment matches any product type or subcollection
    const matchingFilter = serverFilters.find(filter => {
      const filterName = String(filter.name)
      if (filterName !== String(EFilterKeys.PRODUCT_TYPE) && filterName !== String(EFilterKeys.SUBCOLLECTIONS)) {
        return false
      }

      return filter.values.some(value => {
        const valueName = typeof value === 'string' ? value : value.name
        const normalizedValueName = valueName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+/, '')
          .replace(/-+$/, '')
        return normalizedValueName === normalizedLastSegment
      })
    })

    // Only count if there's a match and it's not already in selectedFiltersState
    if (matchingFilter) {
      const filterKey = String(matchingFilter.name)
      const selectedValues = selectedFiltersState[filterKey] || []

      // Don't count if the value is already selected
      const isAlreadySelected = selectedValues.some(value => {
        const normalizedValue = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+/, '')
          .replace(/-+$/, '')
        return normalizedValue === normalizedLastSegment
      })

      return baseCount + (isAlreadySelected ? 0 : 1)
    }

    return baseCount
  }, [selectedFiltersState, showGroupedPage, routerAsPath, serverFilters])
}
